import * as React from 'react';
import { Card } from '@/components/ui/Card';
import { Separator } from '@/components/ui/Separator';
import { QToolbar } from './QToolbar';
import { QTableGrid } from './QTableGrid';
import { QTablePagination } from './QTablePagination';
import { QFilterPanel } from './QFilterPanel';
import { buildColumnConfigs, getVisibleColumns } from './types';
import type { QDataTableProps } from './types';

function QDataTable<T>({
  // Data
  data,
  totalItems,
  isLoading = false,
  getId,

  // Columns
  columns,
  columnConfigs: externalConfigs,
  onColumnConfigsChange,

  // Sorting
  sort,
  order,
  onSortChange,

  // Pagination
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,

  // Search
  search,
  onSearchChange,
  searchPlaceholder,

  // Quick Filters
  quickFilters,
  quickFilterValues,
  onQuickFilterChange,

  // Advanced Filters
  filterSections,
  filterValues = {},
  onApplyFilters,

  // Row Interaction
  onRowClick,
  rowActions,
  selectable = false,
  selectedIds,
  onSelectionChange,

  // Header
  headerActions,
  emptyMessage,
  emptyIcon,
  itemLabel,
}: QDataTableProps<T>) {
  const [filterOpen, setFilterOpen] = React.useState(false);

  // Column configs: use external if provided, otherwise manage internally
  const [internalConfigs, setInternalConfigs] = React.useState(() =>
    buildColumnConfigs(columns, externalConfigs)
  );

  const configs = externalConfigs ?? internalConfigs;
  const setConfigs = onColumnConfigsChange ?? setInternalConfigs;

  const visibleColumns = React.useMemo(
    () => getVisibleColumns(columns, configs),
    [columns, configs]
  );

  // Count active advanced filters
  const activeFilterCount = React.useMemo(() => {
    return Object.values(filterValues).filter((v) => {
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') {
        const obj = v as Record<string, unknown>;
        return obj.from || obj.to;
      }
      return true;
    }).length;
  }, [filterValues]);

  return (
    <Card>
      <QToolbar
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        quickFilters={quickFilters}
        quickFilterValues={quickFilterValues}
        onQuickFilterChange={onQuickFilterChange}
        hasFilters={!!filterSections?.length}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setFilterOpen(true)}
        columns={columns}
        columnConfigs={configs}
        onColumnConfigsChange={setConfigs}
        headerActions={headerActions}
        totalItems={totalItems}
        itemLabel={itemLabel}
      />

      <Separator className="mt-3" />

      <QTableGrid
        data={data}
        columns={visibleColumns}
        getId={getId}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        emptyIcon={emptyIcon}
        sort={sort}
        order={order}
        onSortChange={onSortChange}
        selectable={selectable}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        onRowClick={onRowClick}
        rowActions={rowActions}
      />

      {totalItems > 0 && (
        <>
          <Separator />
          <QTablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            itemLabel={itemLabel}
          />
        </>
      )}

      {filterSections && onApplyFilters && (
        <QFilterPanel
          sections={filterSections}
          values={filterValues}
          onApply={onApplyFilters}
          open={filterOpen}
          onOpenChange={setFilterOpen}
        />
      )}
    </Card>
  );
}

export { QDataTable };
